import React from 'react';
import axios from "../../axios-config";
import { extractFileName } from "../../helper/extractFileName";
import { showToast } from "../../helper/ShowToast";
import { getDuration, getDurationFromUrl, normalizeContentType, pLimit, pollWithBackoff } from '../../helper/uploadUtils';
import { consumeCredits } from './billing';
import { addProcess, completeProcess, failProcess } from './backgroundProcesses';
import { cancelUrStudioHoldAfterFailure } from '../../helper/urstudioPaymentHold';

const handleError = (error) => {
    const message = error.response?.data?.message || error.message;
    const status = error.response?.status;

    if ([400, 401, 403, 500, 502].includes(status)) {
        showToast("error", message);
    } else {
        showToast("error", message);
    }
};

export const startPreview = () => {
    return {
        type: 'STARTPREVIEW',
    }
}

export const previewFail = () => {
    return {
        type: 'PREVIEWFAIL',
    }
}

export const startMaster = () => {
    return {
        type: 'STARTMASTER',
    }
}

export const masterFail = () => {
    return {
        type: 'MASTERFAIL',
    }
}

export const startMastersLibrary = () => {
    return {
        type: 'STARTMASTERSLIBRARY',
    }
}

export const mastersLibraryFail = () => {
    return {
        type: 'MASTERSLIBRARYFAIL',
    }
}

export const previewData = (preview) => {
    return {
        type: 'PREVIEWDATA',
        preview: preview,
    }
}

export const previewDataStatus = (previewStatus) => {
    return {
        type: 'PREVIEWDATASTATUS',
        previewStatus: previewStatus,
    }
}

export const masterData = (master) => {
    return {
        type: 'MASTERDATA',
        master: master,
    }
}

export const masterDataStatus = (masterStatus) => {
    return {
        type: 'MASTERDATASTATUS',
        masterStatus: masterStatus,
    }
}

export const mastersLibraryData = (mastersLibrary) => {
    return {
        type: 'MASTERSLIBRARYDATA',
        mastersLibrary: mastersLibrary,
    }
}

export const taskMetaData = (taskMeta) => {
    return {
        type: 'TASKMETADATA',
        taskMeta: taskMeta,
    }
}

export const originalTrackData = (data) => {
    return {
        type: 'ORIGINALTRACKDATA',
        originalTracks: data,
    }
}

export const uploadData = (data) => {
    return {
        type: 'UPLOADDATA',
        uploadedData: data,
    }
}

export const masteredPreviewData = (data) => {
    return {
        type: 'MASTEREDPREVIEWDATA',
        masteredPreviewData: data,
    }
}

export const cleanAudioData = (data) => {
    return {
        type: 'CLEANAUDIODATA',
        cleanAudio: data,
    }
}

export const saveCleanedAudioData = (data) => {
    return {
        type: 'SAVECLEANEDAUDIO',
        saveCleanedAudio: data,
    }
}

export const mixPreviewData = (data) => {
    return {
        type: 'MIXPREVIEWDATA',
        mixPreview: data,
    }
}

export const mixTrackMetaData = (data) => {
    return {
        type: 'MIXTRACKMETADATA',
        mixMetaData: data,
    }
}

export const stemsMetaData = (data) => {
    return {
        type: 'STEMSMETADATA',
        stemsData: data,
    }
}

export const setUploadFileData = (data) => {
    return {
        type: 'UPLOADFILEDATA',
        payload: data,
    }
}

export const setMixReportId = (data) => {
    return {
        type: 'MIXREPORTID',
        payload: data,
    }
}

export const updateUploadStatuses = (statuses) => ({
    type: "UPDATE_UPLOAD_STATUSES",
    payload: statuses
});

export const clearUploadData = () => ({
    type: 'CLEAR_UPLOAD_DATA',
});

// Persist navigation / ongoing process helpers
export const setLastActiveGoal = (goalId) => ({
    type: 'SET_LAST_ACTIVE_GOAL',
    lastActiveGoal: goalId,
});

export const setOngoingProcess = (process) => ({
    type: 'SET_ONGOING_PROCESS',
    process,
});

export const updateProcessStatus = (processIdOrTaskIds, statusUpdate) => ({
    type: 'UPDATE_ONGOING_PROCESS',
    processIdOrTaskIds,
    statusUpdate,
});

export const clearOngoingProcess = (processId) => ({
    type: 'CLEAR_ONGOING_PROCESS',
    processId,
});

export const mixedLibraryData = (data) => {
    return {
        type: 'MIXEDLIBRARYDATA',
        mixedLibrary: data,
    }
}

function normalizeUploadIds(ids) {
    if (!ids) return [];
    return Array.isArray(ids) ? ids : [ids];
}

const normalizeMimeType = (mimetype, filename = '') => {
    if (filename) {
        const ext = filename.split('.').pop().toLowerCase().trim();

        const extMapping = {
            'wav': 'audio/wav',
            'flac': 'audio/flac',
            'mp3': 'audio/mpeg',
            'mpeg': 'audio/mpeg',
            'm4a': 'audio/mpeg',
            'aac': 'audio/mpeg',
            'ogg': 'audio/mpeg',
            'opus': 'audio/mpeg',
            'webm': 'audio/mpeg',
            'aif': 'audio/aiff',
            'aiff': 'audio/aiff',
        };

        if (extMapping[ext]) {
            return extMapping[ext];
        }
    }

    //  Mimetype-based normalization as fallback
    if (!mimetype || typeof mimetype !== 'string') {
        return 'audio/mpeg';
    }

    const normalized = mimetype.toLowerCase().trim();

    //  Normalize WAV variations
    if (
        normalized === 'audio/wav' ||
        normalized === 'audio/wave' ||
        normalized === 'audio/x-wav' ||
        normalized === 'audio/vnd.wave'
    ) {
        return 'audio/wav';
    }

    //  Normalize MP3/MPEG variations
    if (
        normalized === 'audio/mpeg' ||
        normalized === 'audio/mp3' ||
        normalized === 'audio/mpeg3' ||
        normalized === 'audio/x-mpeg' ||
        normalized === 'audio/x-mp3'
    ) {
        return 'audio/mpeg';
    }

    // Normalize FLAC variations
    if (
        normalized === 'audio/flac' ||
        normalized === 'audio/x-flac'
    ) {
        return 'audio/flac';
    }

    // Normalize AIFF variations
    if (
        normalized === 'audio/aiff' ||
        normalized === 'audio/x-aiff' ||
        normalized === 'audio/aif' ||
        normalized === 'audio/x-aif' ||
        normalized === 'sound/aiff' ||
        normalized === 'application/x-aiff'
    ) {
        return 'audio/aiff';
    }

    // Map other formats to closest ROEX-compatible type
    const formatMapping = {
        // M4A / AAC
        'audio/m4a': 'audio/mpeg',
        'audio/x-m4a': 'audio/mpeg',
        'audio/aac': 'audio/mpeg',
        'audio/aacp': 'audio/mpeg',
        // OGG / OPUS / WEBM
        'audio/ogg': 'audio/mpeg',
        'audio/opus': 'audio/mpeg',
        'audio/webm': 'audio/mpeg',
    };

    if (formatMapping[normalized]) {
        return formatMapping[normalized];
    }

    return 'audio/mpeg';
};

const authCode = "Bearer " + localStorage.getItem("authToken");

const CONCURRENT_PARTS = 3; // upload 3 parts at a time per file

const uploadWithRetry = async (fn, retries = 3) => {
    for (let i = 0; i < retries; i++) {
        try {
            return await fn();
        } catch (err) {
            if (i === retries - 1) throw err;
            await new Promise(r => setTimeout(r, 1000 * (i + 1))); // 1s, 2s, 3s backoff
        }
    }
};

const uploadSingleFile = async (file, urlInfo) => {
    try {
        const [uploadResult, duration] = await Promise.all([
            //Multipart upload
            urlInfo.isMultipart ?
                (async () => {
                    const { partUrls, partSize, uploadId, s3Key } = urlInfo;

                    // Split file into chunks and upload parts in parallel (5 at a time)
                    const partTasks = partUrls.map((partUrl, i) => async () => {
                        const start = i * partSize;
                        const chunk = file.slice(start, start + partSize);
                        console.debug(`${file.name} part ${i + 1}/${partUrls.length} uploading...`);

                        const res = await uploadWithRetry(() =>
                            fetch(partUrl, {
                                method: 'PUT',
                                body: chunk,
                                // no headers at all — exactly what the presigned URL was signed for
                            }).then(response => {
                                if (!response.ok) throw new Error(`Part ${i + 1} failed: ${response.status}`);
                                console.debug(`${file.name} part ${i + 1}/${partUrls.length} done`);
                                return response;
                            })
                            // axios.put(partUrl, chunk, {
                            //     headers: { },
                            //     withCredentials: false,
                            //     timeout: 900000,
                            //     transformRequest: [(data, headers) => {
                            //         delete headers.common?.Authorization;
                            //         delete headers.Authorization;
                            //         return data;
                            //     }],
                            //     onUploadProgress: (evt) => {
                            //         const pct = Math.round((evt.loaded * 100) / evt.total);
                            //         console.debug(`${file.name} part ${i + 1}/${partUrls.length}: ${pct}%`);
                            //     },
                            // })
                        )

                        return {
                            PartNumber: i + 1,
                            ETag: res.headers.get('etag'),   // S3 returns ETag per part
                        };
                    })

                    const parts = await pLimit(partTasks, CONCURRENT_PARTS);

                    // Tell S3 to assemble the parts
                    await axios.post(
                        '/audio-engineer/v1/complete-multipart-upload',
                        { s3Key, uploadId, parts },
                        { headers: { 'Content-Type': 'application/json' } }
                    );
                })()
                // S3 direct upload 
                : (async () => {
                    const response = await fetch(urlInfo.presignedUrl, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': urlInfo.contentType,
                        },
                        body: file,
                    });

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error("S3 Error Response:", errorText);

                        throw new Error(
                            `Upload failed: ${response.status} ${response.statusText}`
                        );
                    }

                    return response;
                })(),

            file.type.startsWith('audio/')
                ? getDuration(file)
                : Promise.resolve(null),
            // Promise.resolve(null)
        ]);

        return {
            s3Url: urlInfo.s3Url,
            s3Key: urlInfo.s3Key,
            filename: file.name,
            contentType: normalizeMimeType(file.type, file.name),
            size: file.size,
            duration: duration != null ? Number(duration).toFixed(2) : null,
            uploadSuccess: true,
            fromUrl: false,
        };

    } catch (error) {
        console.error(`Upload failed for ${file.name}:`, error);
        return {
            filename: file.name,
            uploadSuccess: false,
            error: error.message,
        };
    }
};

export const uploadAndGetPreview = (
    toast,
    body,
    setIsLoading,
    operation = '',
    appendExisting = false,
    docId = '',
    navigate,
) => {
    return async (dispatch, getState) => {

        let files = [];

        if (Array.isArray(body)) {
            files = body.filter(f => f instanceof File);
        } else if (body instanceof FormData) {
            for (const [key, value] of body.entries()) {
                if (key === 'files' || key === 'files[]' || key.startsWith('files')) {
                    if (value instanceof File) files.push(value);
                }
            }
        }

        if (files.length === 0) {
            showToast('error', 'No valid files selected');
            return;
        }

        const MAX_FILES = 32;
        const MAX_FILE_SIZE_MB = 500;
        const MAX_TOTAL_SIZE_MB = 2000;

        if (files.length > MAX_FILES) {
            showToast('error', `Too many files. Maximum allowed is ${MAX_FILES}.`);
            return;
        }

        const oversizedFiles = files.filter(f => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            showToast('error', `Files exceed ${MAX_FILE_SIZE_MB} MB: ${oversizedFiles.map(f => f.name).join(', ')}`);
            return;
        }

        const totalSize = files.reduce((sum, f) => sum + f.size, 0);
        if (totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
            showToast('error', `Total upload size exceeds ${MAX_TOTAL_SIZE_MB} MB.`);
            return;
        }

        setIsLoading(true);
        if (appendExisting) {
            try {
                const state = getState();
                const existing = state?.audioEngineer?.uploadedData;
                // build new placeholder entries
                const placeholders = files.map(file => ({
                    filename: file.name,
                    roexStatus: 'LOCAL_UPLOADING',
                    operation
                }));

                if (existing) {
                    if (Array.isArray(existing.data)) {
                        dispatch(uploadData({
                            data: [...existing.data, ...placeholders],
                        }));
                    } else if (Array.isArray(existing)) {
                        dispatch(uploadData([...existing, ...placeholders]));
                    } else {
                        dispatch(uploadData({ data: placeholders }));
                    }
                } else {
                    dispatch(uploadData({ data: placeholders }));
                }
            } catch (e) {
                dispatch(uploadData({
                    data: files.map(file => ({
                        filename: file.name,
                        roexStatus: 'LOCAL_UPLOADING',
                        operation
                    })),
                }));
            }
        } else {
            dispatch(uploadData({
                data: files.map(file => ({
                    filename: file.name,
                    roexStatus: 'LOCAL_UPLOADING',
                    operation
                })),
            }));
        }

        let stopPolling = null;

        try {
            const presignedResponse = await axios.post(
                '/audio-engineer/v1/generate-presigned-urls',
                {
                    files: files.map(file => ({
                        filename: file.name,
                        contentType: normalizeMimeType(file.type, file.name),
                        size: file.size,
                    })),
                },
                { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
            );

            const { presignedUrls } = presignedResponse.data;

            if (!presignedUrls || presignedUrls.length === 0) {
                throw new Error('Failed to get presigned URLs from server');
            }

            const uploadTasks = files.map((file, index) => () =>
                uploadSingleFile(file, presignedUrls[index])
            );

            const uploadedFiles = await pLimit(uploadTasks, 3);
            const successfulUploads = uploadedFiles.filter(f => f.uploadSuccess);

            if (successfulUploads.length === 0) {
                throw new Error('All S3 uploads failed. Check your network and try again.');
            }

            if (successfulUploads.length < files.length) {
                const failed = uploadedFiles.filter(f => !f.uploadSuccess).map(f => f.filename);
                showToast('warning', `Some files failed to upload: ${failed.join(', ')}`);
            }

            let processUrl = '/audio-engineer/v1/process-uploaded-files';
            if (operation) processUrl += `?operation=${encodeURIComponent(operation)}`;

            const response = await axios.post(
                processUrl,
                { uploadedFiles: successfulUploads },
                { headers: { 'Content-Type': 'application/json' }, timeout: 300000 }
            );

            if (!response.data) throw new Error('Empty response from process-uploaded-files');

            const processId = dispatch(addProcess({
                name: `File Upload process of ${uploadedFiles?.length} file(s)`,
                type: operation === "mixing-engineer" ? 'mixing' : operation === "mix-enhancer" ? 'enhancement' : 'audio_mastering',
                redirectPath:
                    operation === "mixing-engineer"
                        ? '/admin/urstudio/mixing-engineer?tab=settings'
                        : operation === "mix-enhancer"
                            ? '/admin/urstudio/mix-enhancer?tab=settings'
                            : '/admin/urstudio/audio-engineer?tab=settings',
                metadata: { fileCount: uploadedFiles?.length },
            })).payload.id;

            const newMixId = response?.data?.mixesUpload?._id;
            const prevMixId = getState()?.audioEngineer?.mixMetaData?.id;
            const isNewMixProject =
                newMixId && prevMixId && String(newMixId) !== String(prevMixId);

            if (isNewMixProject) {
                dispatch(mixPreviewData({}));
                dispatch(uploadData(response.data));
            } else if (appendExisting) {
                try {
                    const state = getState();
                    const existing = state?.audioEngineer?.uploadedData;

                    if (existing && Array.isArray(response.data?.data)) {
                        const existingItems = Array.isArray(existing.data)
                            ? existing.data
                            : Array.isArray(existing) ? existing : [];

                        const newIds = new Set(
                            response.data.data.map(item => item._id).filter(Boolean)
                        );
                        const keptExisting = existingItems.filter(
                            item => item._id && !newIds.has(item._id)
                        );

                        const merged = [...keptExisting, ...response.data.data];

                        dispatch(uploadData(
                            Array.isArray(existing.data)
                                ? { ...response.data, data: merged }
                                : merged
                        ));
                    } else {
                        dispatch(uploadData(response.data));
                    }
                } catch {
                    dispatch(uploadData(response.data));
                }
            } else {
                dispatch(uploadData(response.data));
            }

            if (operation === 'mixing-engineer' || operation === 'mix-enhancer') {
                if (isNewMixProject) {
                    dispatch(mixPreviewData({}));
                }
                dispatch(mixTrackMetaData({
                    id: newMixId,
                    projectName: response?.data?.mixesUpload?.projectName,
                    projectImage: response?.data?.mixesUpload?.projectImage,
                }));
            }

            const ids = response?.data?.data?.map(d => d._id).filter(Boolean) ?? [];

            if (ids.length > 0) {
                stopPolling = pollWithBackoff(
                    // pollFn
                    async () => {
                        const res = await axios.post(
                            '/audio-engineer/v1/get-upload-status',
                            { ids },
                            { headers: { 'Content-Type': 'application/json' } }
                        );
                        return res.data.data;
                    },
                    // onStatusUpdate
                    (statuses) => dispatch(updateUploadStatuses(statuses)),
                    // isDone
                    (statuses) => statuses.every(s =>
                        s.roexStatus === 'UPLOADED_TO_ROEX' ||
                        s.roexStatus === 'FAILED'
                    ),
                    // onFinished
                    () => {
                        try {
                            setIsLoading(false);
                            dispatch(completeProcess(processId));
                        } catch {
                            if (processId) {
                                dispatch(failProcess(processId, 'Polling timeout'));
                            }
                        }
                    }
                );
            } else {
                setIsLoading(false);
            }

        } catch (error) {
            console.error('uploadAndGetPreview error:', error);
            handleError(error);
            if (stopPolling) stopPolling();
            try { setIsLoading(false); } catch { /* ignore */ }
        }
    };
};

export const uploadFileFromUrls = (
    body,
    setIsLoading,
    operation = '',
    navigate,
    appendExisting = false,
) => {
    return async (dispatch, getState) => {

        const sourceUrls = body?.sourceUrls;

        if (!sourceUrls || !Array.isArray(sourceUrls) || sourceUrls.length === 0) {
            console.error('No source URLs provided');
            return;
        }

        setIsLoading(true);

        if (appendExisting) {
            try {
                const state = getState();
                const existing = state?.audioEngineer?.uploadedData;
                const placeholders = sourceUrls.map(url => ({
                    filename: url.split('/').pop() || url,
                    roexStatus: 'LOCAL_UPLOADING',
                    operation
                }));

                if (existing) {
                    if (Array.isArray(existing.data)) {
                        dispatch(uploadData({ data: [...existing.data, ...placeholders] }));
                    } else if (Array.isArray(existing)) {
                        dispatch(uploadData([...existing, ...placeholders]));
                    } else {
                        dispatch(uploadData({ data: placeholders }));
                    }
                } else {
                    dispatch(uploadData({ data: placeholders }));
                }
            } catch (e) {
                dispatch(uploadData({
                    data: sourceUrls.map(url => ({
                        filename: url.split('/').pop() || url,
                        roexStatus: 'LOCAL_UPLOADING',
                        operation
                    })),
                }));
            }
        }

        let stopPolling = null;

        try {
            const presignedResponse = await axios.post(
                '/audio-engineer/v1/get-presigned-url-from-urls',
                { sourceUrls },
                { headers: { 'Content-Type': 'application/json' }, timeout: 300000 }
            );

            const { copiedFiles } = presignedResponse.data;

            if (!copiedFiles || copiedFiles.length === 0) {
                throw new Error('Failed to copy files to S3');
            }

            const durationTasks = copiedFiles.map((copiedFile) => async () => {
                const contentType = normalizeContentType(copiedFile.contentType);
                if (!contentType.startsWith('audio/')) return null;
                return getDurationFromUrl(copiedFile.sourceUrl, copiedFile.filename);
            });

            const durations = await pLimit(durationTasks, 5);

            const uploadedFiles = copiedFiles.map((copiedFile, index) => {
                const duration = durations[index];
                // Convert "MM:SS" format to seconds
                let durationInSeconds = null;
                if (duration && typeof duration === 'string') {
                    const parts = duration.split(':');
                    if (parts.length === 2) {
                        const minutes = parseInt(parts[0], 10);
                        const seconds = parseInt(parts[1], 10);
                        durationInSeconds = (minutes * 60 + seconds).toFixed(2);
                    }
                }

                return {
                    s3Url: copiedFile.s3Url,
                    s3Key: copiedFile.s3Key,
                    filename: copiedFile.filename,
                    contentType: normalizeContentType(copiedFile.contentType),
                    size: copiedFile.size,
                    duration: Number(durationInSeconds),
                    uploadSuccess: true,
                    fromUrl: true,
                };
            });

            let processUrl = '/audio-engineer/v1/process-uploaded-files';
            if (operation) processUrl += `?operation=${encodeURIComponent(operation)}`;

            const response = await axios.post(
                processUrl,
                { uploadedFiles },
                { headers: { 'Content-Type': 'application/json' }, timeout: 300000 }
            );

            if (!response.data) throw new Error('Empty response from process-uploaded-files');

            const newMixId = response?.data?.mixesUpload?._id;
            const prevMixId = getState()?.audioEngineer?.mixMetaData?.id;
            const isNewMixProject =
                newMixId && prevMixId && String(newMixId) !== String(prevMixId);

            if (isNewMixProject) {
                dispatch(mixPreviewData({}));
                dispatch(uploadData(response.data));
            } else if (appendExisting) {
                try {
                    const state = getState();
                    const existing = state?.audioEngineer?.uploadedData;

                    if (existing && Array.isArray(response.data?.data)) {
                        const existingItems = Array.isArray(existing.data)
                            ? existing.data
                            : Array.isArray(existing) ? existing : [];

                        const newIds = new Set(
                            response.data.data.map(item => item._id).filter(Boolean)
                        );
                        const keptExisting = existingItems.filter(
                            item => item._id && !newIds.has(item._id)
                        );

                        const merged = [...keptExisting, ...response.data.data];

                        dispatch(uploadData(
                            Array.isArray(existing.data)
                                ? { ...response.data, data: merged }
                                : merged
                        ));
                    } else {
                        dispatch(uploadData(response.data));
                    }
                } catch {
                    dispatch(uploadData(response.data));
                }
            } else {
                dispatch(uploadData(response.data));
            }

            if (operation === 'mixing-engineer') {
                if (isNewMixProject) {
                    dispatch(mixPreviewData({}));
                }
                dispatch(mixTrackMetaData({
                    id: newMixId,
                    projectName: response?.data?.mixesUpload?.projectName,
                    projectImage: response?.data?.mixesUpload?.projectImage,
                }));
            }

            const ids = response?.data?.data?.map(d => d._id).filter(Boolean) ?? [];

            if (ids.length > 0) {
                stopPolling = pollWithBackoff(
                    // pollFn
                    async () => {
                        const res = await axios.post(
                            '/audio-engineer/v1/get-upload-status',
                            { ids },
                            { headers: { 'Content-Type': 'application/json' } }
                        );
                        return res.data.data;
                    },
                    // onStatusUpdate
                    (statuses) => dispatch(updateUploadStatuses(statuses)),
                    (statuses) => statuses.every(s =>
                        s.roexStatus === 'UPLOADED_TO_ROEX' ||
                        s.roexStatus === 'FAILED'
                    ),
                    // onFinished — navigate away once all files are processed
                    () => {
                        try { setIsLoading(false); } catch { /* ignore */ }
                        if (operation === 'mastering') {
                            navigate('/admin/urstudio/audio-engineer?tab=settings');
                        } else {
                            navigate('/admin/urstudio/mixing-engineer?tab=multitrack');
                        }
                    }
                );
            } else {
                setIsLoading(false);
            }

        } catch (error) {
            console.error('uploadFileFromUrls error:', error);
            handleError(error);
            if (stopPolling) stopPolling();
            try { setIsLoading(false); } catch { }
        }
    };
};

function pollUploads(uploadIds, dispatch, processId, onComplete) {
    if (!Array.isArray(uploadIds) || uploadIds.length === 0) {
        console.warn("No uploadIds provided for polling.");
        onComplete?.([]);
        return () => { };
    }

    let activeIds = [...uploadIds];
    let isFetching = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 60;
    const INTERVAL_TIME = 10000;

    const authCode = "Bearer " + localStorage.getItem("authToken");

    const interval = setInterval(async () => {

        if (activeIds.length === 0) {
            clearInterval(interval);
            onComplete?.([]);
            return;
        }

        if (attempts >= MAX_ATTEMPTS) {
            clearInterval(interval);
            console.warn("Polling stopped due to timeout.");
            // Fail process if timeout
            if (processId) {
                dispatch(failProcess(processId, 'Polling timeout'));
            }
            onComplete?.([]);
            return;
        }

        if (isFetching) return;

        isFetching = true;
        attempts++;

        try {
            const idsParam =
                activeIds.length > 1
                    ? activeIds.join(",")
                    : activeIds[0];

            const response = await axios.get(
                `/audio-engineer/v1/get-preview-data/${idsParam}`,
                {
                    headers: {
                        Authorization: authCode,
                        "Content-Type": "application/json"
                    }
                }
            );

            let results = response.data?.data || [];

            if (!Array.isArray(results)) {
                results = [results];
            }

            dispatch(previewData(results));

            const remaining = results
                .filter(item => item.status?.toLowerCase() !== "completed")
                .map(item => item._id);

            if (remaining.length === 0) {
                clearInterval(interval);
                onComplete?.(results);
                return;
            }

            activeIds = remaining;

        } catch (err) {
            showToast('error', err.message)
            console.error("Polling error:", err.message);
            // 🔔 Fail process on polling error
            if (processId && attempts >= 3) {  // Only fail after 3 attempts
                dispatch(failProcess(processId, err.message));
            }
        } finally {
            isFetching = false;
        }

    }, INTERVAL_TIME);

    return () => clearInterval(interval);
}

export const getPreviewData = (ids) => {
    return async (dispatch) => {
        try {
            const idsParam =
                ids.length > 1
                    ? ids.join(",")
                    : ids[0];

            const response = await axios.get(
                `/audio-engineer/v1/get-preview-data/${idsParam}`,
                {
                    headers: {
                        Authorization: authCode,
                        "Content-Type": "application/json"
                    }
                }
            );

            let results = response.data?.data || [];

            if (!Array.isArray(results)) {
                results = [results];
            }

            dispatch(previewData(results));
        } catch (error) {
            showToast('error', error)
        }
    }
}

export const createMasteringPreview = (toast, body, setIsLoading, showNotif = true) => {
    return async (dispatch) => {
        setIsLoading(true);

        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');

            const response = await axios.post(
                '/audio-engineer/v1/create-mastering-preview',
                body,
                {
                    headers: {
                        Authorization: authCode,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (response.data) {
                const taskIds = response.data.data.map(item => item.taskId);
                const uploadIds = normalizeUploadIds(
                    response.data.data.map(item => item._id)
                );
                const trackUrls = response.data.data.map(item => item.trackUrl);

                dispatch(taskMetaData({ taskIds, trackUrls }));

                dispatch(previewData({
                    failed: response.data.failed,
                    successful: response.data.successful
                }));
                let processId = null;
                if (showNotif) {
                    processId = dispatch(addProcess({
                        name: `Mastering Preview of ${uploadIds.length} file(s)`,
                        type: 'audio_mastering',
                        redirectPath: '/admin/urstudio/audio-engineer?tab=preview',
                        metadata: { ids: uploadIds, dataType: 'mastered-preview', fileCount: uploadIds.length },
                    })).payload.id;
                }

                // Poll with notification support
                pollUploads(uploadIds, dispatch, processId, () => {
                    setIsLoading(false);
                    dispatch(completeProcess(processId));
                });
            }

        } catch (error) {
            showToast('error', error)
            setIsLoading(false);
        }
    };
};

export const getMasteredPreview = (toast, body) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/audio-engineer/v1/get-preview-data/${body.id}`, {
                headers: { Authorization: authCode, "Content-Type": "application/json" }
            });
            if (response.data) {
                dispatch(previewData(response.data));
            }
        } catch (error) {
            handleError(toast, error);
        }
    };
};

export const fetchMasteredTracks = (toast, body) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post('/audio-engineer/v1/retrieve-mastered-tracks', body, {
                headers: { Authorization: authCode, "Content-Type": "application/json" }
            });
            if (response.data) {
                const failed = response.data?.failed ?? 0;
                const successful = response.data?.successful ?? 0;
                const originalUrls = response.data.data.map(item => item.originalUrl);
                if (failed === 0) {
                    const payload = Object.assign({}, response.data);
                    if (body && body._pollToken !== undefined) payload._pollToken = body._pollToken;
                    dispatch(originalTrackData({ originalUrls }));
                    dispatch(previewData(payload));
                } else {
                    return;
                }
            }
        } catch (error) {
            handleError(toast, error);
        }
    };
};

export const fetchFinalMasteredTracks = (toast, body, setIsLoading) => {
    return async (dispatch) => {
        setIsLoading(true);
        dispatch(startPreview());

        const pollMasteredTrackStatus = (masteredUploadId, onComplete) => {
            let attempts = 0;
            let consecutiveErrors = 0;
            let isHandlingCompletion = false;

            const MAX_POLL_ATTEMPTS = 75;
            const MAX_CONSECUTIVE_ERRORS = 3;

            const interval = setInterval(async () => {
                attempts++;

                if (attempts >= MAX_POLL_ATTEMPTS) {
                    clearInterval(interval);
                    try { setIsLoading(false); } catch (e) { }
                    onComplete?.({});
                    showToast("error", "Final mastered track is taking too long. Please check back later.");
                    return;
                }

                try {
                    const statusRes = await axios.post(
                        '/audio-engineer/v1/get-upload-status',
                        { ids: [masteredUploadId], model: 'Upload' },
                        { headers: { "Content-Type": "application/json" } }
                    );

                    consecutiveErrors = 0;

                    const record = statusRes.data?.data?.[0];

                    if (!record || !record.found) return;

                    if (record.status === 'failed') {
                        clearInterval(interval);
                        try { setIsLoading(false); } catch (e) { }
                        dispatch(previewFail());
                        onComplete?.({});
                        showToast("error", "Final mastered track processing failed. Please try again.");
                        return;
                    }

                    if (record.isMastered && record.masteredUrl && !isHandlingCompletion) {
                        isHandlingCompletion = true;
                        clearInterval(interval);
                        try { setIsLoading(false); } catch (e) { }
                        onComplete?.({ data: record });
                        showToast("success", "Final mastered track is ready!");
                        dispatch(previewData({ data: record }));
                        dispatch(fetchMastersLibrary(toast, setIsLoading));
                    }

                } catch (err) {
                    console.error("Final mastered track polling error:", err);
                    consecutiveErrors++;

                    if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                        clearInterval(interval);
                        try { setIsLoading(false); } catch (e) { }
                        dispatch(previewFail());
                        showToast("error", "Lost connection while checking mastered track status. Please refresh.");
                    }
                }

            }, 10000);

            return () => clearInterval(interval);
        };

        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');

            const response = await axios.post(
                '/audio-engineer/v1/retrieve-final-mastered',
                body,
                { headers: { Authorization: authCode, "Content-Type": "application/json" } }
            );

            const masteredUploadId = response.data?.data?.[0]?.uploadId;

            if (!masteredUploadId) {
                throw new Error("No masteredUploadId returned from server");
            }
            const processId = dispatch(addProcess({
                name: `Final Mastered of ${masteredUploadId.length} file(s)`,
                type: 'audio_mastering',
                redirectPath: '/admin/urstudio/audio-engineer?tab=preview',
                metadata: { ids: masteredUploadId, dataType: 'mastered-final', fileCount: masteredUploadId.length },
            })).payload.id;

            pollMasteredTrackStatus(masteredUploadId, () => {
                try { setIsLoading(false); } catch (e) { }
                dispatch(completeProcess(processId))
            });

        } catch (error) {
            console.error("fetchFinalMasteredTracks error:", error);
            if (body?.paymentIntentId) {
                await cancelUrStudioHoldAfterFailure(
                    dispatch,
                    body.paymentIntentId,
                    error?.message || 'Master job failed to start'
                );
            }
            dispatch(previewFail());
            showToast("error", error?.response?.data?.error || error.message || "An error occurred");
            try { setIsLoading(false); } catch (e) { }
        }
    };
};

export const getFinalMasteredData = (uploadId) => {
    return async (dispatch) => {
        try {

            const response = await axios.post(
                `/audio-engineer/v1/get-upload-status`,
                { ids: [uploadId], model: 'Upload' },
                {
                    headers: {
                        Authorization: authCode,
                        "Content-Type": "application/json"
                    }
                }
            );

            let results = response.data?.data || [];

            if (!Array.isArray(results)) {
                results = [results];
            }

            dispatch(previewData({ data: results }));
            dispatch(fetchMastersLibrary('', () => { }));
        } catch (error) {
            showToast('error', error)
        }
    }
}

export const getAudioClean = (body, setIsLoading) => {
    return async (dispatch) => {
        setIsLoading(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post('/audio-engineer/v1/audio-cleanup', body, {
                headers: { Authorization: authCode, "Content-Type": "application/json" }
            });
            if (response.data) {
                dispatch(consumeCredits(2))
                setIsLoading(false)
                dispatch(cleanAudioData(response.data));
            }
        } catch (error) {
            showToast("error", error?.response?.data?.error || error.message || "An error occurred");
        } finally {
            setIsLoading(false)
        }
    };
}

export const saveCleanedAudio = (body, setIsLoading) => {
    return async (dispatch) => {
        setIsLoading(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post('/audio-engineer/v1/save-cleaned-audio', body, {
                headers: { Authorization: authCode, "Content-Type": "application/json" }
            });
            if (response.data) {
                setIsLoading(false)
                dispatch(saveCleanedAudioData(response.data));
            }
        } catch (error) {
            showToast("error", error?.response?.data?.error || error.message || "An error occurred");
        } finally {
            setIsLoading(false)
        }
    };
}

function pollMixPreviews(trackId, dispatch, setIsLoading) {
    let attempts = 0;
    let consecutiveErrors = 0;
    let isHandlingCompletion = false;

    const MAX_POLL_ATTEMPTS = 75;
    const MAX_CONSECUTIVE_ERRORS = 3;

    const intervalId = setInterval(async () => {
        attempts++;

        if (attempts >= MAX_POLL_ATTEMPTS) {
            clearInterval(intervalId);
            try { setIsLoading(false); } catch (e) { }
            showToast("error", "Mix preview is taking too long. Please check back later.");
            return;
        }

        try {
            const authCode = "Bearer " + localStorage.getItem("authToken");

            const response = await axios.get(
                `/audio-engineer/v1/get-mixpreview-data/${trackId}`,
                {
                    headers: {
                        Authorization: authCode,
                        "Content-Type": "application/json"
                    }
                }
            );

            const data = response.data?.data;
            if (data?.roex_response?.state) {
                dispatch({
                    type: "UPDATE_MIX_PROCESS_STATE",
                    payload: data.roex_response.state,
                });
            }

            consecutiveErrors = 0;

            if (data?.status === "failed") {
                clearInterval(intervalId);
                try { setIsLoading(false); } catch (e) { }
                showToast("error", "Mix preview processing failed. Please try again.");
                return;
            }

            if (data?.status === "completed" && !isHandlingCompletion) {
                isHandlingCompletion = true;
                clearInterval(intervalId);
                dispatch(retrieveMixPreview({ taskId: trackId }, setIsLoading));
            }

        } catch (err) {
            console.error("Polling error:", err?.message || err);
            consecutiveErrors++;

            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                clearInterval(intervalId);
                try { setIsLoading(false); } catch (e) { }
                showToast("error", "Lost connection while checking mix status. Please refresh.");
            }
        }

    }, 12000);

    return () => clearInterval(intervalId);
}

export const createMixPreview = (body, setIsLoading) => {
    return async (dispatch, getState) => {
        setIsLoading(true);
        dispatch(mixPreviewData({}));
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(
                '/audio-engineer/v1/create-mix-preview',
                body,
                { headers: { Authorization: authCode, "Content-Type": "application/json" } }
            );

            if (response.data) {
                const roexId = response.data?.data?.roex_task_id;

                if (!roexId) {
                    throw new Error("No roex_task_id returned from server");
                }

                pollMixPreviews(roexId, dispatch, setIsLoading);
            }

        } catch (error) {
            showToast("error", error?.response?.data?.error || error.message || "An error occurred");
            try { setIsLoading(false); } catch (e) { }
        }
    };
};

export const retrieveMixPreview = (body, setIsLoading, showNotif = true) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(
                '/audio-engineer/v1/retrieve-mix-preview',
                body,
                { headers: { Authorization: authCode, "Content-Type": "application/json" } }
            );
            let processId = null;
            if (showNotif) {
                processId = dispatch(addProcess({
                    name: `Mix Preview`,
                    type: 'mixing',
                    redirectPath: '/admin/urstudio/mixing-engineer?tab=multitrack',
                    metadata: { body, dataType: 'mix-preview', fileCount: 1 },
                })).payload.id;
            }

            if (response.data) {
                dispatch(mixPreviewData(response.data));
                const mixesDoc = response.data?.data || response.data;
                if (mixesDoc?._id) {
                    dispatch(mixTrackMetaData({
                        id: mixesDoc._id,
                        projectName: mixesDoc.projectName,
                        projectImage: mixesDoc.projectImage,
                    }));
                }
                if (processId) {
                    dispatch(completeProcess(processId));
                }
            }

        } catch (error) {
            showToast("error", error?.response?.data?.error || error.message || "An error occurred");
        } finally {
            try { setIsLoading(false); } catch (e) { }
        }
    };
};

/** Resolve Mongo _id and roex_task_id from mixPreview (API wrapper or nested doc). */
export function resolveMixPreviewIdentity(mixPreview) {
    if (!mixPreview || typeof mixPreview !== 'object') {
        return { _id: null, roex_task_id: null };
    }

    const nested = mixPreview.data;
    const doc =
        nested && typeof nested === 'object' && (nested._id || nested.roex_task_id)
            ? nested
            : null;

    return {
        _id: doc?._id || mixPreview._id || null,
        roex_task_id: doc?.roex_task_id || mixPreview.taskId || null,
    };
}

/** Normalize mix enhancer preview/final doc (flat API doc or { data } wrapper). */
export function resolveEnhanceMixDoc(mixPreview) {
    if (!mixPreview || typeof mixPreview !== 'object') {
        return null;
    }

    const nested = mixPreview.data;
    if (
        nested &&
        typeof nested === 'object' &&
        (nested._id || nested.mixedUrl || nested?.masteredUrl || nested.mixedPreviewUrl || nested.roex_task_id)
    ) {
        return nested;
    }

    return mixPreview;
}

/** Best download URL from Redux enhancer state (final mix preferred over preview). */
export function resolveEnhanceDownloadUrl(mixPreview) {
    const doc = resolveEnhanceMixDoc(mixPreview);
    if (doc?.isFinalMixed || doc?.isMixEnhanced) {
        return doc.mixedUrl || doc.mixedPreviewUrl || doc?.masteredUrl || null;
    }
    return doc?.mixedPreviewUrl || doc?.mixedUrl || doc?.masteredUrl || null;
}

/** Build retrieve-final-mix body from current Redux state so id and taskId stay paired. */
export function buildRetrieveFinalMixBody(audioEngineerState, options = {}) {
    const { mixPreview, uploadedData, mixMetaData } = audioEngineerState || {};
    const { _id: previewId, roex_task_id } = resolveMixPreviewIdentity(mixPreview);
    const id = mixMetaData?.id || previewId;

    const uploadItems = Array.isArray(uploadedData?.data)
        ? uploadedData.data
        : Array.isArray(uploadedData)
            ? uploadedData
            : [];

    const trackData = uploadItems
        .map((item) => ({ trackURL: item?.readable_url || null }))
        .filter((t) => t.trackURL);

    return {
        id,
        taskId: roex_task_id,
        trackData,
        returnStems: false,
        createMaster: Boolean(options.createMaster),
        desiredLoudness: options.desiredLoudness || 'MEDIUM',
        sampleRate: options.sampleRate || '44100',
    };
}

/** Returns an error message when the body is not safe to send, or null if valid. */
export function validateRetrieveFinalMixBody(body, mixMetaData = {}, mixPreview = null) {
    if (!body?.id) {
        return 'Mix project is missing. Please create a mix preview again.';
    }
    if (!body?.taskId) {
        return 'Mix preview task is missing. Please wait for the preview to finish or create it again.';
    }
    if (!Array.isArray(body.trackData) || body.trackData.length === 0) {
        return 'Tracks are still uploading. Please wait until all tracks are ready before purchasing.';
    }
    if (mixMetaData?.id && String(mixMetaData.id) !== String(body.id)) {
        return 'Your mix session changed after upload. Please refresh and create the preview again.';
    }
    const { _id: previewId, roex_task_id: previewTaskId } = resolveMixPreviewIdentity(mixPreview);
    if (previewId && String(previewId) !== String(body.id)) {
        return 'Mix preview does not match the current project. Please create the preview again.';
    }
    if (previewTaskId && String(previewTaskId) !== String(body.taskId)) {
        return 'Mix preview task is out of date. Please wait for preview to finish or create it again.';
    }
    return null;
}

/** Build create-full-mix-enhance body from Redux + UI enhancement options. */
export function buildFullMixEnhanceBody(audioEngineerState, options = {}) {
    const { uploadedData, mixMetaData, mixPreview } = audioEngineerState || {};
    const { _id: previewId } = resolveMixPreviewIdentity(mixPreview);
    const id = mixMetaData?.id || previewId || options.id || null;

    const uploadItems = Array.isArray(uploadedData?.data)
        ? uploadedData.data
        : Array.isArray(uploadedData)
            ? uploadedData
            : [];

    const fileLocation =
        uploadItems[0]?.readable_url ||
        options.fileLocation ||
        null;

    return {
        id,
        fileLocation,
        musicalStyle: options.musicalStyle || 'OTHER',
        isMaster: Boolean(options.isMaster),
        fixClippingIssues: Boolean(options.fixClippingIssues),
        fixDRCIssues: Boolean(options.fixDRCIssues),
        fixStereoWidthIssues: Boolean(options.fixStereoWidthIssues),
        fixTonalProfileIssues: Boolean(options.fixTonalProfileIssues),
        fixLoudnessIssues: Boolean(options.fixLoudnessIssues),
        applyMastering: Boolean(options.applyMastering),
        loudnessPreference: options.loudnessPreference || 'NO_CHANGE',
        stemProcessing: Boolean(options.stemProcessing),
    };
}

/** Returns an error message when the full-enhance body is not safe to send, or null if valid. */
export function validateFullMixEnhanceBody(body, mixMetaData = {}, mixPreview = null) {
    if (!body?.id) {
        return 'Mix project is missing. Please upload your track and create a preview again.';
    }
    if (!body?.fileLocation) {
        return 'Track file is still uploading. Please wait until upload finishes.';
    }
    if (mixMetaData?.id && String(mixMetaData.id) !== String(body.id)) {
        return 'Your session changed after upload. Please refresh and create the preview again.';
    }
    const { _id: previewId } = resolveMixPreviewIdentity(mixPreview);
    if (previewId && String(previewId) !== String(body.id)) {
        return 'Preview does not match the current project. Please create the preview again.';
    }
    return null;
}

/** Build retrieve-enhanced-track body; taskId from poll overrides stale mixPreview task. */
export function buildRetrieveEnhancedMixBody(audioEngineerState, options = {}) {
    const { mixMetaData, mixPreview } = audioEngineerState || {};
    const { _id: previewId, roex_task_id } = resolveMixPreviewIdentity(mixPreview);
    const id = mixMetaData?.id || options.id || previewId;
    const taskId = options.taskId || roex_task_id;

    return {
        id,
        taskId,
        isMaster: Boolean(options.isMaster),
    };
}

/** Validates retrieve-enhanced-track body (id only — taskId changes after full enhance). */
export function validateRetrieveEnhancedMixBody(body, mixMetaData = {}, mixPreview = null) {
    if (!body?.id) {
        return 'Mix project is missing. Please create an enhance preview again.';
    }
    if (!body?.taskId) {
        return 'Enhance task is missing. Please wait for processing to finish.';
    }
    if (mixMetaData?.id && String(mixMetaData.id) !== String(body.id)) {
        return 'Your session changed. Please refresh and try again.';
    }
    const { _id: previewId } = resolveMixPreviewIdentity(mixPreview);
    if (previewId && String(previewId) !== String(body.id)) {
        return 'Preview does not match the current project. Please create the preview again.';
    }
    return null;
}

export const retrieveFinalMix = (body, setIsLoading) => {
    return async (dispatch, getState) => {
        setIsLoading(true);

        const pollFinalMixStatus = (mixesUploadId) => {
            const MAX_POLL_ATTEMPTS = 150;
            const MAX_CONSECUTIVE_ERRORS = 6;
            const POLL_INTERVAL_MS = 15_000;

            let attempts = 0;
            let consecutiveErrors = 0;
            let isHandlingCompletion = false;

            const processId = dispatch(addProcess({
                name: `Final Mix`,
                type: 'mixing',
                redirectPath: '/admin/urstudio/mixing-engineer?tab=multitrack',
                metadata: { ids: mixesUploadId, dataType: 'mix-final', fileCount: 1 },
            })).payload.id;

            const interval = setInterval(async () => {
                attempts++;

                if (attempts >= MAX_POLL_ATTEMPTS) {
                    clearInterval(interval);
                    try { setIsLoading(false); } catch (e) { }
                    showToast("error", "Final mix is taking too long. Please check back later.");
                    return;
                }

                try {
                    const statusRes = await axios.post(
                        '/audio-engineer/v1/get-upload-status',
                        { ids: [mixesUploadId], model: 'MixesUpload' },
                        { headers: { "Content-Type": "application/json" } }
                    );

                    consecutiveErrors = 0;

                    const record = statusRes.data?.data?.[0];

                    if (!record || !record.found) return;

                    // const processId = dispatch(addProcess({
                    //     name: `Final Mix`,
                    //     type: 'mixing',
                    //     redirectPath: '/admin/urstudio/mixing-engineer?tab=multitrack',
                    //     metadata: { ids: mixesUploadId, dataType: 'mix-final', fileCount: 1 },
                    // })).payload.id;

                    if (record.status === 'failed') {
                        clearInterval(interval);
                        try { setIsLoading(false); } catch (e) { }
                        showToast("error", "Final mix processing failed. Please try again.");
                        return;
                    }

                    if (record.isFinalMixed && record.mixedUrl && !isHandlingCompletion) {
                        isHandlingCompletion = true;
                        clearInterval(interval);
                        try { setIsLoading(false); } catch (e) { }
                        showToast("success", "Final mix is ready!");
                        dispatch(mixPreviewData({ data: record }));
                        dispatch(mixTrackMetaData({
                            id: record._id,
                            projectName: record.projectName,
                            projectImage: record.projectImage,
                        }));
                        if (processId) {
                            dispatch(completeProcess(processId));
                        }
                    }

                } catch (err) {
                    console.error("Final mix polling error:", err);
                    consecutiveErrors++;

                    if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                        clearInterval(interval);
                        try { setIsLoading(false); } catch (e) { }
                        showToast("error", "Lost connection while checking mix status. Please refresh.");
                    }
                }

            }, POLL_INTERVAL_MS);

            return () => clearInterval(interval);
        };

        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const { audioEngineer } = getState();
            const requestBody = {
                ...buildRetrieveFinalMixBody(audioEngineer, {
                    createMaster: body?.createMaster,
                    desiredLoudness: body?.desiredLoudness,
                    sampleRate: body?.sampleRate,
                }),
            };

            const validationError = validateRetrieveFinalMixBody(
                requestBody,
                audioEngineer?.mixMetaData,
                audioEngineer?.mixPreview
            );
            if (validationError) {
                showToast('error', validationError);
                setIsLoading(false);
                return;
            }

            const response = await axios.post(
                '/audio-engineer/v1/retrieve-final-mix',
                requestBody,
                { headers: { Authorization: authCode, "Content-Type": "application/json" } }
            );

            const mixesUploadId = response.data?.mixesUploadId || requestBody?.id;

            if (!mixesUploadId) {
                throw new Error("No mixesUploadId returned from server");
            }
            
            pollFinalMixStatus(mixesUploadId);

        } catch (error) {
            console.error("retrieveFinalMix error:", error);
            if (body?.paymentIntentId) {
                await cancelUrStudioHoldAfterFailure(
                    dispatch,
                    body.paymentIntentId,
                    error?.message || 'Mix job failed to start'
                );
            }
            const apiMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error.message ||
                'An error occurred';
            showToast('error', apiMessage);
            try { setIsLoading(false); } catch (e) { }
        }
    };
};

export const getFinalMixedData = (uploadId) => {
    return async (dispatch) => {
        try {
            const response = await axios.post(
                `/audio-engineer/v1/get-upload-status`,
                { ids: [uploadId], model: 'MixesUpload' },
                {
                    headers: {
                        Authorization: authCode,
                        "Content-Type": "application/json"
                    }
                }
            );

            let results = response.data?.data || [];

            if (!Array.isArray(results)) {
                results = [results];
            }
            dispatch(previewData({ data: results }));
        } catch (error) {
            showToast('error', error)
        }
    }
}

export const renameMixTrack = (id, body, fetchLibrary) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/audio-engineer/v1/mixes-library/${id}/rename`, body, {
                headers: { Authorization: authCode },
            });
            if (response.data) {
                if (fetchLibrary) {
                    dispatch(fetchMixedLibrary(() => { }));
                } else {
                    dispatch(mixTrackMetaData({
                        id: response?.data?.data._id,
                        projectName: response?.data?.data.projectName,
                        projectIamge: response?.data?.data.projectImage
                    }));
                }
            }
        } catch (error) {
            showToast("error", error?.response?.data?.error || error.message || "An error occurred");
        }
    }
};

export const uploadProjectImage = (body) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/audio-engineer/v1/mixes-library/update-project-image`, body, {
                headers: { Authorization: authCode },
                'multipart/form-data': true
            });
            if (response.data) {
                dispatch(mixTrackMetaData({
                    id: response?.data?.data._id,
                    projectName: response?.data?.data.projectName,
                    projectImage: response?.data?.data.projectImage
                }));
            }
        } catch (error) {
            showToast("error", error?.response?.data?.error || error.message || "An error occurred");
        }
    }
};

export const fetchMixedLibrary = (setIsLoading) => {
    return async (dispatch) => {
        setIsLoading(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get('/audio-engineer/v1/mixes-library', {
                headers: { Authorization: authCode },
            });
            if (response.data) {
                dispatch(mixedLibraryData(response.data));
                setIsLoading(false)
            }
        } catch (error) {
            showToast("error", error?.response?.data?.error || error.message || "An error occurred");
        } finally {
            setIsLoading(false)
        }
    }
};

export const createAudioPreview = (toast, body, setIsLoading) => {
    return async (dispatch) => {
        setIsLoading(true)
        dispatch(startPreview());
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post('/audio-engineer/v1/create-preview', body, {
                headers: { Authorization: authCode, "Content-Type": "multipart/form-data" }
            });
            if (response.data) {
                dispatch(previewData(response.data.data));
                setIsLoading(false)
                if (response.data.data.previewId) {
                    dispatch(fetchPreviewStatus(toast, response.data.data.previewId, setIsLoading))
                }
            }
        } catch (error) {
            dispatch(previewFail());
            handleError(toast, error);
        } finally {
            setIsLoading(false)
        }
    };
};

export const fetchPreviewStatus = (toast, previewId, setIsLoading) => {
    return async (dispatch) => {
        setIsLoading(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/audio-engineer/v1/preview/${previewId}/status`, {
                headers: { Authorization: authCode },
            });
            if (response.data) {
                dispatch(previewDataStatus(response.data));
                setIsLoading(false)
            }
        } catch (error) {
            handleError(toast, error);
        } finally {
            setIsLoading(false)
        }
    };
}

export const createAudioMaster = (toast, body, setIsLoading) => {
    return async (dispatch) => {
        setIsLoading(true)
        dispatch(startMaster());
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post('/audio-engineer/v1/create-master', body, {
                headers: { Authorization: authCode }
            });
            if (response.data) {
                dispatch(masterData(response.data.data));
                setIsLoading(false)
                if (response.data.data.masterId) {
                    dispatch(fetchMasterStatus(toast, response.data.data.masterId, setIsLoading))
                }
            }
        } catch (error) {
            dispatch(masterFail());
            handleError(toast, error);
        } finally {
            setIsLoading(false)
        }
    };
};

export const fetchMasterStatus = (toast, previewId, setIsLoading) => {
    return async (dispatch) => {
        setIsLoading(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/audio-engineer/v1/master/${previewId}/status`, {
                headers: { Authorization: authCode },
            });
            if (response.data) {
                dispatch(previewDataStatus(response.data));
                dispatch(fetchMastersLibrary(toast, setIsLoading))
                setIsLoading(false)
            }
        } catch (error) {
            handleError(toast, error);
        } finally {
            setIsLoading(false)
        }
    };
}

export const fetchMastersLibrary = (toast, setIsLoading) => {
    return async (dispatch) => {
        setIsLoading(true)
        dispatch(startMastersLibrary());
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get('/audio-engineer/v1/masters-library?limit=40', {
                headers: { Authorization: authCode },
            });
            if (response.data) {
                dispatch(mastersLibraryData(response.data));
                setIsLoading(false)
            }
        } catch (error) {
            dispatch(mastersLibraryFail());
            handleError(toast, error);
        } finally {
            setIsLoading(false)
        }
    }
};

export const searchMastersLibrary = (toast, query, setIsLoading) => {
    return async (dispatch) => {
        // setIsLoading(true)
        dispatch(startMastersLibrary());
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/audio-engineer/v1/masters-library/search?q=${query}`, {
                headers: { Authorization: authCode },
            });
            if (response.data) {
                dispatch(mastersLibraryData(response.data));
                // setIsLoading(false)
            }
        } catch (error) {
            dispatch(mastersLibraryFail());
            handleError(toast, error);
        }
    }
};

export const deleteMasterTrack = (toast, trackId, setIsLoading, onDone) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.delete(`/audio-engineer/v1/masters-library/${trackId}`, {
                headers: { Authorization: authCode },
            });
            if (response.data) {
                await dispatch(fetchMastersLibrary(toast, setIsLoading))
            }
        } catch (error) {
            handleError(toast, error);
        } finally {
            onDone?.()
        }
    }
};

export const renameMasterTrack = (toast, trackId, body, setIsLoading, onDone) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.put(`/audio-engineer/v1/masters-library/${trackId}/rename`, body, {
                headers: { Authorization: authCode },
            });
            if (response.data) {
                await dispatch(fetchMastersLibrary(toast, setIsLoading))
            }
        } catch (error) {
            handleError(toast, error);
        } finally {
            onDone?.()
        }
    }
};

export const downloadMasterTrack = (body, setIsLoading) => {
    return async () => {
        try {
            if (!body?.url || !body?.format) {
                showToast('error', 'Download is not ready yet. Please wait for processing to finish or use Mixes Library.');
                try { setIsLoading(false); } catch (e) { }
                return;
            }

            setIsLoading(true)
            const authCode = "Bearer " + localStorage.getItem("authToken");

            const response = await axios.post(
                `/audio-engineer/v1/convert-file`,
                body,
                {
                    headers: { Authorization: authCode },
                    responseType: "blob", // important for binary
                }
            );

            // Use provided name or fallback
            const baseName = body.fileName
                ? extractFileName(body.fileName)
                : "converted";
            const format = body.format || "audio";

            // Create blob & download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${baseName}.${format}`); // track-based filename
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setIsLoading(false)
        } catch (error) {
            if (error) showToast('error', error?.response?.data?.error || "Download failed");
            try { setIsLoading(false); } catch (e) { }
        }
    };
};

/** Download enhancer mix — resolves URL from Redux, fetches from API if stale on 2nd run. */
export const downloadEnhancerTrack = (format, setIsLoading) => {
    return async (dispatch, getState) => {
        const { audioEngineer } = getState();
        const { mixPreview, mixMetaData } = audioEngineer || {};
        const doc = resolveEnhanceMixDoc(mixPreview);
        let url = resolveEnhanceDownloadUrl(mixPreview);
        let fileName = doc?.projectName || mixMetaData?.projectName ||doc?.filename;

        if (!url) {
            try {
                const authCode = "Bearer " + localStorage.getItem("authToken");
                const { roex_task_id: taskId } = resolveMixPreviewIdentity(mixPreview);
                const mixesUploadId = mixMetaData?.id || doc?._id;

                if (taskId) {
                    const response = await axios.get(
                        `/audio-engineer/v1/get-mixenhance-data/${taskId}`,
                        { headers: { Authorization: authCode } }
                    );
                    const fresh = response.data?.data;
                    url = fresh?.mixedUrl || fresh?.mixedPreviewUrl;
                    fileName = fileName || fresh?.projectName;
                    if (fresh) {
                        dispatch(mixPreviewData(fresh));
                    }
                } else if (mixesUploadId) {
                    const response = await axios.get(
                        `/audio-engineer/v1/mixes-library`,
                        {
                            headers: { Authorization: authCode },
                            params: { id: mixesUploadId, type: 'all' },
                        }
                    );
                    const fresh = response.data?.data;
                    url = fresh?.mixedUrl || fresh?.mixedPreviewUrl;
                    fileName = fileName || fresh?.projectName;
                }
            } catch (err) {
                console.error('downloadEnhancerTrack fetch fallback:', err?.message || err);
            }
        }

        if (!url) {
            showToast('error', 'Download is not ready yet. Please wait for processing to finish or use Mixes Library.');
            try { setIsLoading(false); } catch (e) { }
            return;
        }

        await dispatch(downloadMasterTrack({ url, format, fileName }, setIsLoading));
    };
};

export const sendFile = (toast, body) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            await axios.post(`/audio-engineer/v1/send-file`, body, {
                headers: { Authorization: authCode },
            });
        } catch (error) {
            handleError(toast, error);
        }
    }
};

/* Mix Analyst API */
// ─── Action 1: Upload files to S3 ────────────────────────────────────────────
export const uploadFilesToS3 = (files, setIsLoading, onSuccess) => {
    return async (dispatch) => {
        if (!Array.isArray(files) || files.length === 0) {
            showToast('error', 'No valid files selected');
            return;
        }

        setIsLoading(true);

        dispatch(uploadData({
            data: files.map(file => ({
                filename: file.name,
                roexStatus: 'LOCAL_UPLOADING',
                operation: 'mix-analysis'
            })),
        }));

        const processId = dispatch(addProcess({
            name: `Mix Analyst File Upload of ${files.length} file`,
            type: 'mix-analyst',
            redirectPath: '/admin/urstudio/mix-analyst?tab=upload',
            metadata: { fileCount: files.length },
        })).payload.id;

        try {
            const presignedResponse = await axios.post(
                '/audio-engineer/v1/generate-presigned-urls',
                {
                    files: files.map(file => ({
                        filename: file.name,
                        contentType: normalizeMimeType(file.type, file.name),
                        size: file.size,
                    })),
                    type: 'mix-analysis'
                },
                { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
            );

            const { presignedUrls } = presignedResponse.data;

            if (!presignedUrls || presignedUrls.length === 0) {
                throw new Error('Failed to get presigned URLs from server');
            }

            const uploadTasks = files.map((file, index) => () =>
                uploadSingleFile(file, presignedUrls[index])
            );

            const uploadedFiles = await pLimit(uploadTasks, 3);
            const successfulUploads = uploadedFiles.filter(f => f.uploadSuccess);

            if (successfulUploads.length === 0) {
                throw new Error('All S3 uploads failed. Check your network and try again.');
            }

            if (successfulUploads.length < files.length) {
                const failed = uploadedFiles.filter(f => !f.uploadSuccess).map(f => f.filename);
                showToast('warning', `Some files failed to upload: ${failed.join(', ')}`);
            }

            dispatch(uploadData({
                data: successfulUploads.map(file => ({
                    ...file,
                    roexStatus: 'LOCAL_UPLOADED',
                    operation: 'mix-analysis'
                })),
            }));
            dispatch(completeProcess(processId));

            // Hand off directly — no dispatch return value needed
            onSuccess(successfulUploads);

        } catch (error) {
            console.error('uploadFilesToS3 error:', error);
            handleError(error);
            setIsLoading(false);
        }
    };
};

export const processUploadedFiles = (
    successfulUploads,
    setIsLoading,
    operation = '',
    setStopPollingCallback = null
) => {
    return async (dispatch) => {
        if (!successfulUploads || successfulUploads.length === 0) {
            showToast('error', 'No uploaded files to process');
            setIsLoading(false);
            return;
        }

        let stopPolling = null;

        try {
            let processUrl = '/audio-engineer/v1/process-uploaded-files';
            if (operation) processUrl += `?operation=${encodeURIComponent(operation)}`;

            const response = await axios.post(
                processUrl,
                { uploadedFiles: successfulUploads },
                { headers: { 'Content-Type': 'application/json' }, timeout: 300000 }
            );

            if (!response.data) throw new Error('Empty response from process-uploaded-files');

            dispatch(uploadData(response.data));

            const ids = response?.data?.data?.map(d => d._id).filter(Boolean) ?? [];

            if (ids.length > 0) {
                stopPolling = pollWithBackoff(
                    async () => {
                        const res = await axios.post(
                            '/audio-engineer/v1/get-upload-status',
                            { ids },
                            { headers: { 'Content-Type': 'application/json' } }
                        );
                        return res.data.data;
                    },
                    (statuses) => dispatch(updateUploadStatuses(statuses)),
                    (statuses) => statuses.every(s =>
                        s.roexStatus === 'UPLOADED_TO_ROEX' ||
                        s.roexStatus === 'FAILED'
                    ),
                    () => {
                        try { setIsLoading(false); } catch { /* ignore */ }
                    }
                );
                if (typeof setStopPollingCallback === 'function') {
                    setStopPollingCallback(stopPolling);
                }
            } else {
                setIsLoading(false);
            }

        } catch (error) {
            console.error('processUploadedFiles error:', error);
            handleError(error);
            if (stopPolling) stopPolling();
            try { setIsLoading(false); } catch { /* ignore */ }
        }
    };
};

/* Mix Enhancer APIs */
function pollMixEnhancePreviews(id, trackId, dispatch, setIsLoading, getFullLengthMix, isMaster, processId, paymentIntentId = null) {
    let attempts = 0;
    let consecutiveErrors = 0;
    let isHandlingCompletion = false;

    const MAX_POLL_ATTEMPTS = 80;
    const MAX_CONSECUTIVE_ERRORS = 3;

    const intervalId = setInterval(async () => {
        attempts++;

        if (attempts >= MAX_POLL_ATTEMPTS) {
            clearInterval(intervalId);
            try { setIsLoading(false); } catch (e) { }
            showToast("error", "Mix Enhance preview is taking too long. Please check back later.");
            return;
        }

        try {
            const authCode = "Bearer " + localStorage.getItem("authToken");

            const response = await axios.get(
                `/audio-engineer/v1/get-mixenhance-data/${trackId}`,
                {
                    headers: {
                        Authorization: authCode,
                        "Content-Type": "application/json"
                    }
                }
            );

            const data = response.data?.data;
            if (data?.roex_response?.state) {
                dispatch({
                    type: "UPDATE_MIX_PROCESS_STATE",
                    payload: data.roex_response.state,
                });
            }

            consecutiveErrors = 0;

            if (data?.status === "failed") {
                clearInterval(intervalId);
                try { setIsLoading(false); } catch (e) { }
                if (processId) {
                    dispatch(failProcess(processId, 'Polling timeout'));
                }
                if (paymentIntentId) {
                    cancelUrStudioHoldAfterFailure(
                        dispatch,
                        paymentIntentId,
                        'Mix enhance preview processing failed'
                    );
                }
                showToast("error", "Mix Enhance preview processing failed. Please try again.");
                return;
            }

            if (data?.status === "completed" && !isHandlingCompletion) {
                isHandlingCompletion = true;
                clearInterval(intervalId);
                if (getFullLengthMix) {
                    dispatch(retrieveEnhancedMix({ id, taskId: trackId, isMaster, paymentIntentId }, setIsLoading));
                } else {
                    try { setIsLoading(false); } catch (e) { }
                    dispatch(mixPreviewData(data));
                    if (data?._id) {
                        dispatch(mixTrackMetaData({
                            id: data._id,
                            projectName: data.projectName,
                            projectImage: data.projectImage,
                        }));
                    }
                    if (data?.stemProcessing && data?.stems) {
                        dispatch(stemsMetaData(data?.stems));
                    }
                    showToast("success", "Enhanced Mix ready.");
                    dispatch(completeProcess(processId));
                }
            }

        } catch (err) {
            console.error("Polling error:", err?.message || err);
            consecutiveErrors++;

            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                clearInterval(intervalId);
                try { setIsLoading(false); } catch (e) { }
                showToast("error", "Lost connection while checking mix status. Please refresh.");
            }
        }

    }, 12000);

    return () => clearInterval(intervalId);
}

function pollFullMixEnhance(trackId, dispatch, setIsLoading, isMaster, processId) {
    let attempts = 0;
    let consecutiveErrors = 0;
    let isHandlingCompletion = false;

    const MAX_POLL_ATTEMPTS = 80;
    const MAX_CONSECUTIVE_ERRORS = 6;

    const intervalId = setInterval(async () => {
        attempts++;

        if (attempts >= MAX_POLL_ATTEMPTS) {
            clearInterval(intervalId);
            try { setIsLoading(false); } catch (e) { }
            showToast("error", "Mix Enhance preview is taking too long. Please check back later.");
            return;
        }

        try {
            const authCode = "Bearer " + localStorage.getItem("authToken");
            const api_url = isMaster ? `/audio-engineer/v1/get-masterenhance-data/${trackId}` : `/audio-engineer/v1/get-mixenhance-data/${trackId}`

            const response = await axios.get(
                api_url,
                {
                    headers: {
                        Authorization: authCode,
                        "Content-Type": "application/json"
                    }
                }
            );

            const data = response.data?.data;
            if (data?.roex_response?.state) {
                dispatch({
                    type: "UPDATE_MIX_PROCESS_STATE",
                    payload: data.roex_response.state,
                });
            }

            consecutiveErrors = 0;

            if (data?.status === "failed") {
                clearInterval(intervalId);
                try { setIsLoading(false); } catch (e) { }
                showToast("error", "Mix Enhance processing failed. Please try again.");
                if (processId) {
                    dispatch(failProcess(processId, 'Mix Enhance processing failed. Please try again.'));
                }
                return;
            }

            if (data?.isMixEnhanced && !isHandlingCompletion) {
                isHandlingCompletion = true;
                clearInterval(intervalId);
                try { setIsLoading(false); } catch (e) { }
                dispatch(mixPreviewData(data));
                if (data?._id) {
                    dispatch(mixTrackMetaData({
                        id: data._id,
                        projectName: data.projectName,
                        projectImage: data.projectImage,
                    }));
                }
                if (data?.stems?.length) {
                    dispatch(stemsMetaData(data.stems));
                }
                dispatch(completeProcess(processId));
            }

        } catch (err) {
            console.error("Polling error:", err?.message || err);
            consecutiveErrors++;

            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                clearInterval(intervalId);
                try { setIsLoading(false); } catch (e) { }
                showToast("error", "Lost connection while checking mix status. Please refresh.");
            }
        }

    }, 30000);

    return () => clearInterval(intervalId);
}

export const createMixEnhancePreview = (body, setIsLoading, setMaster) => {
    return async (dispatch, getState) => {
        setIsLoading(true);
        dispatch(mixPreviewData({}));
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const { audioEngineer } = getState();
            const requestBody = {
                ...body,
                ...buildFullMixEnhanceBody(audioEngineer, body),
            };
            const validationError = validateFullMixEnhanceBody(
                requestBody,
                audioEngineer?.mixMetaData,
                audioEngineer?.mixPreview
            );
            if (validationError) {
                showToast("error", validationError);
                try { setIsLoading(false); } catch (e) { }
                return;
            }

            const response = await axios.post(
                '/audio-engineer/v1/create-mix-enhance-preview',
                requestBody,
                { headers: { Authorization: authCode, "Content-Type": "application/json" } }
            );

            if (response.data) {
                const roexId = response.data?.data?.roex_task_id;

                if (!roexId) {
                    throw new Error("No roex_task_id returned from server");
                }

                const processId = dispatch(addProcess({
                    name: `Mix Enhance Preview of 1 file(s)`,
                    type: 'enhancement',
                    redirectPath: '/admin/urstudio/mix-enhancer?tab=settings&type=preview',
                    metadata: { response, fileCount: 1 },
                })).payload.id;

                pollMixEnhancePreviews(requestBody.id, roexId, dispatch, setIsLoading, false, requestBody.applyMastering, processId);
                setTimeout(() => {
                    setMaster(true);
                }, 3000);
            }

        } catch (error) {
            showToast("error", error?.response?.data?.error || error.message || "An error occurred");
            try {
                setMaster(false);
                setIsLoading(false);
            } catch (e) { }
        }
    };
};

export const retrieveEnhancedMix = (body, setIsLoading) => {
    return async (dispatch, getState) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const { audioEngineer } = getState();
            const requestBody = buildRetrieveEnhancedMixBody(audioEngineer, {
                id: body?.id,
                taskId: body?.taskId,
                isMaster: body?.isMaster,
            });
            if (body?.paymentIntentId) {
                requestBody.paymentIntentId = body.paymentIntentId;
            }
            const validationError = validateRetrieveEnhancedMixBody(
                requestBody,
                audioEngineer?.mixMetaData,
                audioEngineer?.mixPreview
            );
            if (validationError) {
                showToast("error", validationError);
                try { setIsLoading(false); } catch (e) { }
                return;
            }

            const response = await axios.post(
                '/audio-engineer/v1/retrieve-enhanced-track',
                requestBody,
                { headers: { Authorization: authCode, "Content-Type": "application/json" } }
            );

            if (response.data) {
                const processId = dispatch(addProcess({
                    name: `Final Mix Enhance of 1 file(s)`,
                    type: 'enhancement',
                    redirectPath: '/admin/urstudio/mix-enhancer?tab=settings&type=final',
                    metadata: { response, fileCount: 1 },
                })).payload.id;

                pollFullMixEnhance(requestBody.taskId, dispatch, setIsLoading, requestBody.isMaster, processId);
            }

        } catch (error) {
            showToast("error", error?.response?.data?.error || error.message || "An error occurred");
            try { setIsLoading(false); } catch (e) { }
        }
    };
};

export const createFullEnhancedMix = (body, setIsLoading) => {
    return async (dispatch, getState) => {
        setIsLoading(true);
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const { audioEngineer } = getState();
            const requestBody = {
                ...body,
                ...buildFullMixEnhanceBody(audioEngineer, body),
            };
            const validationError = validateFullMixEnhanceBody(
                requestBody,
                audioEngineer?.mixMetaData,
                audioEngineer?.mixPreview
            );
            if (validationError) {
                showToast("error", validationError);
                try { setIsLoading(false); } catch (e) { }
                return;
            }

            const response = await axios.post(
                '/audio-engineer/v1/create-full-mix-enhance',
                requestBody,
                { headers: { Authorization: authCode, "Content-Type": "application/json" } }
            );

            if (response.data) {
                const roexId = response.data?.data?.roex_task_id;

                if (!roexId) {
                    throw new Error("No roex_task_id returned from server");
                }

                pollMixEnhancePreviews(
                    requestBody.id,
                    roexId,
                    dispatch,
                    setIsLoading,
                    true,
                    requestBody.applyMastering,
                    null,
                    body?.paymentIntentId || null
                );
            }

        } catch (error) {
            if (body?.paymentIntentId) {
                await cancelUrStudioHoldAfterFailure(
                    dispatch,
                    body.paymentIntentId,
                    error?.message || 'Enhance job failed to start'
                );
            }
            showToast("error", error?.response?.data?.error || error.message || "An error occurred");
            try { setIsLoading(false); } catch (e) { }
        }
    };
};
